import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { CHOICE_KEYS } from '@Services/Editor/Constants';

import {
  addEmptyChoiceVisibleConditionItems,
  addEmptyChoiceRequiredItems,
  setIsEventFinish,
  duplicateChoice,
  deleteChoice,
  deleteChoiceProperty,
  addEmptyChoiceRequiredSponsorCookieItems,
} from '@Services/Editor';
import useContextMenu from '@Hooks/useContextMenu';

import { ReactComponent as MoreIconSVG } from '@Assets/Svgs/ic_more.svg';
import NoticeDeleteChoiceModal from './NoticeDeleteChoiceModal';
import ChoiceShowMoreContextMenu from './ChoiceShowMoreContextMenu';

const ChoiceShowMoreButton = ({ processId, choiceId }) => {
  const dispatch = useDispatch();
  const choices = useSelector(({ editor }) => editor?.processes[processId].choices);
  const targetChoice = useSelector(({ editor }) => editor?.processes[processId].choices[choiceId]);

  const [isNoticeDeleteChoiceModalVisible, setIsNoticeDeleteChoiceModalVisible] = useState(false);
  const { isContextMenuVisible, handleShowContextMenuClick } = useContextMenu();

  const isShowChoiceVisibleCondition = !!targetChoice.hide;
  const isShowChoiceRequired = !!targetChoice.required;
  const isShowChoiceRequiredSponsorCookie = !!targetChoice.requiredSponsorCookie;
  const isShowNoticeDeleteChoiceModal = !isNoticeDeleteChoiceModalVisible && choices.length === 1;

  const handleDeleteChoicePropertyClick = useCallback(
    type => {
      dispatch(deleteChoiceProperty({ processId, choiceId, type }));
    },
    [dispatch],
  );

  const handleSetIsEventFinish = useCallback(() => {
    dispatch(setIsEventFinish({ processId }));
  }, [dispatch]);

  const handleAddChoiceVisibleConditionClick = useCallback(() => {
    if (isShowChoiceVisibleCondition) {
      handleDeleteChoicePropertyClick(CHOICE_KEYS.HIDE);
      return;
    }

    dispatch(addEmptyChoiceVisibleConditionItems({ processId, choiceId }));
  }, [targetChoice, dispatch]);

  const handleAddChoiceRequiredClick = useCallback(() => {
    if (isShowChoiceRequired) {
      handleDeleteChoicePropertyClick(CHOICE_KEYS.REQUIRED);
      return;
    }

    dispatch(addEmptyChoiceRequiredItems({ processId, choiceId }));
  }, [targetChoice, dispatch]);

  // 쿠키 후원
  const handleAddChoiceRequiredSponsorCookieClick = useCallback(() => {
    if (isShowChoiceRequiredSponsorCookie) {
      handleDeleteChoicePropertyClick(CHOICE_KEYS.REQUIRED_SPONSOR_COOKIE);
      return;
    }

    dispatch(addEmptyChoiceRequiredSponsorCookieItems({ processId, choiceId }));
  }, [targetChoice, dispatch]);

  const handleDuplicateChoiceClick = useCallback(() => {
    dispatch(duplicateChoice({ processId, choices: targetChoice }));
  }, [targetChoice, dispatch]);

  const handleDeleteChoice = useCallback(() => {
    dispatch(deleteChoice({ processId, choiceId }));
  }, [dispatch]);

  const handleIsNoticeDeleteChoiceModalVisible = useCallback(() => {
    setIsNoticeDeleteChoiceModalVisible(!isNoticeDeleteChoiceModalVisible);
  }, [isNoticeDeleteChoiceModalVisible]);

  const handleDeleteChoiceButtonClick = useCallback(() => {
    if (isShowNoticeDeleteChoiceModal) {
      handleIsNoticeDeleteChoiceModalVisible();
    } else {
      handleDeleteChoice();
    }
  }, [isNoticeDeleteChoiceModalVisible, choices]);

  const handleDeleteChoiceButtonOfModalClick = () => {
    handleSetIsEventFinish();
    handleDeleteChoice();
    handleIsNoticeDeleteChoiceModalVisible();
  };

  return (
    <>
      <ShowMoreButton onClick={event => handleShowContextMenuClick(event)}>
        <ShowMoreIcon />
      </ShowMoreButton>

      {isContextMenuVisible && (
        <ChoiceShowMoreContextMenu
          processId={processId}
          choiceId={choiceId}
          isShowChoiceVisibleCondition={isShowChoiceVisibleCondition}
          isShowChoiceRequired={isShowChoiceRequired}
          isShowChoiceRequiredSponsorCookie={isShowChoiceRequiredSponsorCookie}
          handleAddChoiceVisibleConditionClick={handleAddChoiceVisibleConditionClick}
          handleAddChoiceRequiredClick={handleAddChoiceRequiredClick}
          handleAddChoiceRequiredSponsorCookieClick={handleAddChoiceRequiredSponsorCookieClick}
          handleDuplicateChoiceClick={handleDuplicateChoiceClick}
          handleDeleteChoiceButtonClick={handleDeleteChoiceButtonClick}
          choices={choices}
        />
      )}

      <NoticeDeleteChoiceModal
        isNoticeDeleteChoiceModalVisible={isNoticeDeleteChoiceModalVisible}
        handleDeleteChoiceButtonOfModalClick={handleDeleteChoiceButtonOfModalClick}
        handleIsNoticeDeleteChoiceModalVisible={handleIsNoticeDeleteChoiceModalVisible}
      />
    </>
  );
};

ChoiceShowMoreButton.propTypes = {
  processId: PropTypes.number.isRequired,
  choiceId: PropTypes.number.isRequired,
};

const ShowMoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 32px;
  height: 32px;

  border: none;
  background: none;

  cursor: pointer;
`;

const ShowMoreIcon = styled(MoreIconSVG)`
  width: 16px;
  height: 16px;
`;

export default ChoiceShowMoreButton;
